import { forwardRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowLeft, ArrowUpRight, Images, Ticket, ZoomIn } from "lucide-react";
import { Container } from "@/components/shared/PageHeader";
import { PhotoCredit } from "@/components/shared/PhotoCredit";
import { PhotoNumberTag } from "@/components/shared/PhotoNumber";
import { EventImage } from "@/components/shared/EventImage";
import { useImageAlts } from "@/hooks/use-image-alts";
import { fmtDate, fmtRange, fmtTime } from "@/lib/format";
import { displayFor } from "@/lib/display";
import { EventCountdown } from "@/components/events/EventCountdown";
import { AddToCalendar } from "@/components/events/AddToCalendar";
import { ShareMenu } from "@/components/events/ShareMenu";
import { countdownState } from "@/lib/media";
import { EventWeather, useEventWeather } from "@/components/events/EventWeather";
import { useDominantColor } from "@/hooks/use-dominant-color";
import { cn } from "@/lib/utils";

/** Display size follows the length of the title, so a long one breaks into two calm lines instead of four ragged ones. */
export const titleSize = (title = "") => {
  const n = title.trim().length;
  if (n <= 22) return "text-[clamp(44px,6.4vw,96px)] leading-[0.94] max-w-[14ch]";
  if (n <= 44) return "text-[clamp(36px,4.9vw,74px)] leading-[0.98] max-w-[18ch] sm:max-w-[20ch]";
  return "text-[clamp(30px,3.8vw,58px)] leading-[1.02] max-w-[20ch] sm:max-w-[24ch]";
};

const Fact = ({ label, children, sub, testId }) => (
  <div className="min-w-0" data-testid={testId}>
    <dt className="t-mono text-muted-foreground">{label}</dt>
    <dd className="mt-1.5 text-[16px] font-medium leading-snug sm:text-[17px]">{children}</dd>
    {sub && <dd className="mt-0.5 text-[13px] text-muted-foreground">{sub}</dd>}
  </div>
);

/** Poster-style date mark for events without a photograph: «04» over «november · onsdag». */
const DateStamp = ({ ev }) => {
  const d1 = fmtDate(ev.date, "dd");
  const d2 = ev.end_date && ev.end_date !== ev.date ? fmtDate(ev.end_date, "dd") : null;
  return (
    <div className="hidden shrink-0 flex-col items-end text-right lg:flex" data-testid="event-hero-datestamp" aria-hidden="true">
      <span className="font-display text-[112px] font-semibold leading-[0.85] tracking-[-0.06em] tabular xl:text-[136px]">{d2 ? `${d1}–${d2}` : d1}</span>
      <span className="t-mono mt-3 text-muted-foreground">{fmtDate(ev.date, "MMMM")}{d2 ? "" : ` · ${fmtDate(ev.date, "EEEE")}`}</span>
    </div>
  );
};

/** Small paper sticker on the photograph: the day, the month – the one thing people remember. */
const DateSticker = ({ ev, inline = false }) => {
  const d2 = ev.end_date && ev.end_date !== ev.date ? fmtDate(ev.end_date, "dd") : null;
  return (
    <div className={cn("flex flex-col items-center rounded-[14px] bg-background/95 px-3.5 py-2.5 text-foreground backdrop-blur-md", inline ? "border border-border" : "absolute left-4 top-4 shadow-[0_8px_24px_-12px_rgba(28,25,23,.5)] sm:left-5 sm:top-5")} data-testid={inline ? "event-hero-date-chip" : "event-hero-date-sticker"} aria-hidden="true">
      <span className="font-display text-[30px] font-semibold leading-none tracking-[-0.04em] tabular sm:text-[34px]">{d2 ? `${fmtDate(ev.date, "dd")}–${d2}` : fmtDate(ev.date, "dd")}</span>
      <span className="t-mono-sm mt-1 text-muted-foreground">{fmtDate(ev.date, "MMM").replace(".", "")}</span>
    </div>
  );
};

/** «2 bilete» / «Sjå biletet» – on the photograph when it fills the frame, beside the poster when it is shown whole. */
const GalleryButton = ({ images, onOpenGallery, inline = false }) => {
  const multi = images.length > 1;
  return (
    <button type="button" onClick={() => onOpenGallery?.(0)} data-testid="event-hero-gallery-button" aria-label={multi ? `Sjå alle ${images.length} bileta` : "Sjå biletet i full storleik"}
      className={cn("inline-flex h-9 items-center gap-2 rounded-full px-3.5 text-[12.5px] font-medium transition-colors duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sol",
        inline ? "bg-ink text-paper hover:bg-sol hover:text-ink" : "absolute bottom-4 right-4 bg-ink/55 text-paper backdrop-blur-md hover:bg-paper hover:text-ink sm:bottom-5 sm:right-5")}>
      {multi ? <><Images className="h-3.5 w-3.5" /> <span className="tabular">{images.length} bilete</span></> : <><ZoomIn className="h-3.5 w-3.5" /> <span>Sjå biletet</span></>}
    </button>
  );
};

const CTA = "inline-flex h-12 items-center gap-2 rounded-full px-6 text-[15px] font-semibold transition-[transform,background-color,color] duration-300 hover:scale-[1.03] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sol";

/**
 * Editorial opening on the paper canvas: the words on the left, the photograph framed on the right – never text on top of
 * a picture. On phones: eyebrow → title → summary → photograph → facts → actions.
 */
export const EventHero = forwardRef(({ ev, images, captions = {}, onOpenGallery }, ref) => {
  const reduce = useReducedMotion();
  const { altFor } = useImageAlts();
  const over = countdownState(ev).state === "over";
  const hasImg = images.length > 0;
  const glow = useDominantColor(hasImg ? images[0] : null);
  const { data: weather } = useEventWeather(ev);
  const hasWeather = !!weather?.available;
  const cover = images[0];
  const [resolved, setResolved] = useState(null);
  const display = resolved || displayFor(ev, cover);
  const whole = display.fit === "contain";   // poster mode: the picture is complete, date and gallery sit beside it – never on it
  const time = fmtTime(ev.start_time, ev.end_time);
  const doors = ev.practical?.doors_open;
  const container = { hidden: {}, show: { transition: { staggerChildren: reduce ? 0 : 0.08, delayChildren: 0.05 } } };
  const item = { hidden: { opacity: 0, y: reduce ? 0 : 18 }, show: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.16, 1, 0.3, 1] } } };

  return (
    <section ref={ref} className="relative overflow-hidden bg-background text-foreground" data-testid="event-hero" data-images={images.length}>
      <Container className="pt-5 sm:pt-7">
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.4 }} className="flex items-center justify-between gap-3">
          <Link to="/arrangement" className="inline-flex h-9 items-center gap-2 rounded-full border border-border bg-background px-3.5 text-[13px] font-medium text-foreground transition-colors hover:bg-ink hover:text-paper" data-testid="event-back-link"><ArrowLeft className="h-3.5 w-3.5" /> <span className="hidden sm:inline">Heile programmet</span><span className="sm:hidden">Program</span></Link>
          <div className="flex items-center gap-2">
            <EventCountdown ev={ev} tone="light" />
            <ShareMenu ev={ev} className="h-9 rounded-full border border-border bg-background px-3.5 text-[13px] font-medium hover:bg-ink hover:text-paper" testId="event-hero-share" />
          </div>
        </motion.div>
      </Container>

      <Container>
        <motion.div variants={container} initial="hidden" animate="show" className="grid gap-x-10 gap-y-8 pb-12 pt-8 sm:pt-10 lg:grid-cols-12 lg:gap-x-12 lg:pb-16 lg:pt-12">
          {/* A – the words */}
          <div className={cn("min-w-0", hasImg ? "lg:col-span-7" : "lg:col-span-12")}>
            <div className="flex items-start justify-between gap-10">
              <div className="min-w-0">
                <motion.p variants={item} className="t-mono flex flex-wrap items-center gap-x-2 text-muted-foreground" data-testid="event-hero-eyebrow">
                  {ev.category && <span className="text-sol">{ev.category}</span>}{ev.category && <span aria-hidden="true">·</span>}
                  <span>{fmtDate(ev.date, "EEEE")} {fmtRange(ev.date, ev.end_date)}</span>{time && <span aria-hidden="true">·</span>}{time && <span>{time}</span>}
                </motion.p>
                <motion.h1 variants={item} lang="nn" className={cn("mt-5 font-display font-semibold tracking-[-0.03em] text-balance break-words [hyphens:manual] sm:mt-6", titleSize(ev.title))} data-testid="event-title">{ev.title}</motion.h1>
                {ev.summary && <motion.p variants={item} className="t-lead mt-5 max-w-[58ch] text-foreground/70 text-pretty sm:mt-6" data-testid="event-summary">{ev.summary}</motion.p>}
              </div>
              {!hasImg && <motion.div variants={item}><DateStamp ev={ev} /></motion.div>}
            </div>
          </div>

          {/* The photograph – framed, never behind the words */}
          {hasImg && (
            <motion.figure variants={item} className={cn("relative min-w-0 lg:col-span-5 lg:col-start-8 lg:row-span-2 lg:row-start-1 lg:pl-2", whole && display.ratio < 0.85 && "lg:justify-self-end")} style={whole && display.ratio < 0.85 ? { width: "min(100%, 460px)" } : undefined} data-testid="event-hero-framed-photo" data-display={whole ? "whole" : "fill"}>
              {glow && <div aria-hidden="true" className="pointer-events-none absolute -inset-x-10 -top-10 bottom-10 -z-10 hidden opacity-[0.22] blur-3xl transition-opacity duration-1000 lg:block" style={{ background: `radial-gradient(60% 55% at 60% 40%, ${glow}, transparent 70%)` }} data-testid="event-hero-glow" />}
              <EventImage
                src={cover} display={display} alt={altFor(cover, ev.title)} sizes="(min-width: 1024px) 40vw, 100vw" width={1400} priority hover={!whole} onResolve={setResolved}
                className="group rounded-[20px] border border-border shadow-[0_40px_80px_-40px_rgba(28,25,23,.45)]" imgClassName="rounded-[20px]" testId="event-hero-image-frame"
              >
                {!whole && <DateSticker ev={ev} />}
                {!whole && <GalleryButton images={images} onOpenGallery={onOpenGallery} />}
                {!whole && <PhotoNumberTag src={cover} className="absolute right-3 top-3" testId="event-hero-photo-number" />}
              </EventImage>
              {whole && (
                <div className="mt-3 flex items-center justify-between gap-3" data-testid="event-hero-poster-bar">
                  <DateSticker ev={ev} inline />
                  <span className="flex items-center gap-2">
                    <PhotoNumberTag src={cover} tone="muted" testId="event-hero-photo-number" />
                    <GalleryButton images={images} onOpenGallery={onOpenGallery} inline />
                  </span>
                </div>
              )}
              <figcaption className="mt-3 flex items-baseline justify-between gap-4 px-1 text-[12.5px] leading-snug text-muted-foreground">
                <span className="min-w-0 text-pretty" data-testid="event-hero-caption">{captions[cover] || ""}</span>
                <PhotoCredit src={cover} className="shrink-0" testId="event-hero-photo-credit" />
              </figcaption>
            </motion.figure>
          )}

          {/* B – facts and the one action */}
          <motion.div variants={item} className={cn("min-w-0", hasImg ? "lg:col-span-7 lg:col-start-1" : "lg:col-span-12")}>
            <dl className={cn("grid grid-cols-2 gap-x-6 gap-y-5 border-t border-border pt-6", hasWeather ? "sm:grid-cols-3 lg:grid-cols-5" : "sm:grid-cols-4")} data-testid="event-hero-meta">
              <Fact label="Dato">{fmtRange(ev.date, ev.end_date)}</Fact>
              <Fact label="Tid" sub={doors ? `Dørene opnar kl. ${doors}` : undefined}>{time || "Heile dagen"}</Fact>
              <Fact label="Stad">{ev.location || "Halsnøy Kulturhus"}</Fact>
              <Fact label="Pris" testId={ev.price_text ? "event-price" : undefined}>{ev.price_text || (ev.tickets_enabled ? "Sjå billettar" : "Ope for alle")}</Fact>
              {hasWeather && <EventWeather ev={ev} />}
            </dl>
            <div className="mt-7 flex flex-wrap items-center gap-2.5" data-testid="event-hero-actions">
              {ev.tickets_enabled && !over && <a href="#billettar" className={cn(CTA, "bg-sol text-ink hover:bg-ink hover:text-paper")} data-testid="event-hero-ticket-button"><Ticket className="h-4 w-4" /> {ev.price_text && /gratis/i.test(ev.price_text) ? "Meld deg på" : "Billettar"}</a>}
              {!ev.tickets_enabled && !over && ev.ticket_url && <a href={ev.ticket_url} target="_blank" rel="noopener noreferrer" className={cn(CTA, "bg-sol text-ink hover:bg-ink hover:text-paper")} data-testid="event-hero-ticket-link">Billettar / påmelding <ArrowUpRight className="h-4 w-4" /></a>}
              {!over && <AddToCalendar ev={ev} tone="light" testId="event-hero-calendar" />}
              {over && <span className="text-[14px] text-muted-foreground" data-testid="event-hero-over">Arrangementet er avslutta.</span>}
            </div>
          </motion.div>
        </motion.div>
      </Container>
    </section>
  );
});
EventHero.displayName = "EventHero";
